/**
 * ManualClientShell · iter303ak-fix49 — Manual New-Client intake path.
 * ────────────────────────────────────────────────────────────────────
 * When the operator picks "Manual Intake" at the top of T10 New Client,
 * this component:
 *   1. Auto-POSTs a shell client (`Untitled Client · YYYY-MM-DD`) on mount
 *   2. Auto-creates a bare property linked to the client
 *   3. Renders a card with a big "Open Client Edit" pop-out button
 *   4. On Continue → bubbles the client_id + client_record up to the runner
 *
 * Downstream property/tenant/agent steps read `client_form_choice ==
 * manual_intake` and auto-route to their own manual (goto_form) paths.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, ExternalLink, AlertCircle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function ManualClientShell({ existingClientId, onSave, onCancel, headerTitle, headerDescription }) {
  const [state, setState]         = useState(existingClientId ? 'ready' : 'creating'); // creating | ready | error
  const [clientId, setClientId]   = useState(existingClientId || '');
  const [propertyId, setPropertyId] = useState('');
  const [clientRecord, setClientRecord] = useState(null);
  const [errMsg, setErrMsg]       = useState('');
  const bootstrapped = useRef(false);

  // ── Shell client + property creation ─────────────────────────
  useEffect(() => {
    if (existingClientId) return; // reuse existing
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const shell = {
          name_first: '',
          name_last: `Untitled Client · ${today}`,
          address_num: '', address_street: '', address_suburb: '', address_city: '',
        };
        const cRes = await api.post('/clients', shell);
        const newCid = cRes.data?._id || cRes.data?.id;
        setClientRecord(cRes.data);
        setClientId(newCid);
        // Auto-create empty property linked to the client so downstream
        // steps have a property_id to route to.
        try {
          const pRes = await api.post('/properties', {
            client_id: newCid,
            property_address_num: '', property_address_street: '',
            property_address_suburb: '', property_address_city: '',
            property_comments: 'Auto-created from manual client intake — details pending',
          });
          const newPid = pRes.data?._id || pRes.data?.id;
          setPropertyId(newPid);
          try {
            await api.post('/roof-details', { property_id: newPid, description: 'Main Roof' });
          } catch { /* non-fatal */ }
        } catch (propErr) {
          // Non-fatal — the operator can add a property from the client page later.
          console.warn('Auto-create property failed:', propErr);
        }
        setState('ready');
      } catch (err) {
        setState('error');
        setErrMsg(err?.response?.data?.detail || err?.message || 'Failed to create shell client.');
      }
    })();
  }, [existingClientId]);

  const openEditPage = () => {
    if (!clientId) return;
    window.open(`/clients/${clientId}?edit=client_details&pane=client_details`, '_blank', 'noopener');
  };

  const continueRunner = () => {
    onSave?.({
      client_id: clientId,
      property_id: propertyId,
      client_record: clientRecord,
    });
  };

  return (
    <div className="space-y-4" data-testid="wf-manual-client-shell">
      <div className="bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500 rounded-r-lg p-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-1">
          ✎ {headerTitle || 'Manual Client Intake'}
        </div>
        <div className="text-slate-700 text-sm">
          {headerDescription || "We'll create a shell client and open the full edit page in a pop-out. Fill in the details, save, close the pop-out — this step will auto-tick."}
        </div>
      </div>

      {state === 'creating' && (
        <div className="p-6 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 text-center" data-testid="wf-manual-shell-creating">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-600 mb-2" />
          <div className="text-sm font-semibold text-slate-800">Creating shell client…</div>
          <div className="text-[11px] text-slate-500 mt-1">Placeholder name will be replaced when you edit the client.</div>
        </div>
      )}

      {state === 'error' && (
        <div className="p-4 border-2 border-rose-300 bg-rose-50 rounded-lg" data-testid="wf-manual-shell-error">
          <div className="text-sm font-bold text-rose-900 flex items-center gap-2"><AlertCircle size={14} /> Couldn't create shell client</div>
          <div className="text-xs text-rose-800 mt-1">{errMsg}</div>
          <button onClick={onCancel} className="mt-3 text-xs px-3 py-1.5 bg-white border border-rose-300 rounded text-rose-800 hover:bg-rose-100">Cancel and go back</button>
        </div>
      )}

      {state === 'ready' && (
        <div className="p-4 border-2 border-emerald-300 bg-emerald-50 rounded-lg space-y-3" data-testid="wf-manual-shell-ready">
          <div className="text-sm font-bold text-emerald-900 flex items-center gap-2">
            <CheckCircle2 size={14} /> Shell client created
          </div>
          <div className="text-xs text-slate-700">
            <b>Client ID:</b> <span className="font-mono text-slate-900">{clientId}</span>
            {propertyId && <> · <b>Property ID:</b> <span className="font-mono text-slate-900">{propertyId}</span></>}
          </div>
          <div className="text-xs text-slate-600">
            Placeholder name: <span className="font-mono">Untitled Client · {new Date().toISOString().slice(0, 10)}</span> — rename in the edit page below.
          </div>
          <button onClick={openEditPage}
            data-testid="wf-manual-shell-open-edit"
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 text-sm font-semibold">
            <ExternalLink size={14} /> Open Client Edit Page (new tab)
          </button>
          <div className="text-[11px] text-slate-500">Fill in the details in the pop-out, save, then come back and click Continue.</div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={continueRunner}
          disabled={state !== 'ready'}
          data-testid="wf-manual-shell-continue"
          className="px-4 py-1.5 rounded-md text-sm bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Continue
        </button>
      </div>
    </div>
  );
}
